export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public body: unknown,
  ) {
    super(`API error ${status}: ${statusText}`);
    this.name = 'ApiError';
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  /** Returns the current access token, or null for anonymous requests. */
  getToken?: () => string | null | Promise<string | null>;
  fetch?: typeof fetch;
}

export interface ApiResponseWithMetadata<T> {
  data: T;
  dataSource: string | null;
}

export interface ApiClient {
  readonly baseUrl: string;
  /** Raw request; throws ApiError on a non-2xx status. */
  request<T>(path: string, init?: RequestInit): Promise<T>;
  requestWithMetadata<T>(path: string, init?: RequestInit): Promise<ApiResponseWithMetadata<T>>;
  /** Unwraps Cryptohopper's `{ data }` envelope and surfaces `{ error: 1 }` bodies as ApiError. */
  data<T>(path: string, init?: RequestInit): Promise<T>;
  dataWithMetadata<T>(path: string, init?: RequestInit): Promise<ApiResponseWithMetadata<T>>;
}

/**
 * Minimal fetch wrapper for the Cryptohopper REST API. It knows two things
 * about that API: the token goes in an `access-token` header, and successful
 * bodies are wrapped as `{ "data": ... }` while errors can arrive as
 * `{ status, code, error: 1, message }` — sometimes with HTTP 200, so
 * `response.ok` alone does not catch them (verified live 2026-07-05).
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const baseUrl = options.baseUrl.replace(/\/$/, '');

  async function requestWithMetadata<T>(path: string, init: RequestInit = {}): Promise<ApiResponseWithMetadata<T>> {
    const headers = new Headers(init.headers);
    if (!headers.has('Content-Type') && init.body) headers.set('Content-Type', 'application/json');
    const token = options.getToken ? await options.getToken() : null;
    if (token) headers.set('access-token', token);

    const doFetch = options.fetch ?? globalThis.fetch;
    const response = await doFetch(`${baseUrl}${path}`, { ...init, headers });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ApiError(response.status, response.statusText, body);
    }

    const dataSource = response.headers.get('x-ch-data-source');
    if (response.status === 204) return { data: undefined as T, dataSource };
    return { data: (await response.json()) as T, dataSource };
  }

  async function dataWithMetadata<T>(path: string, init?: RequestInit): Promise<ApiResponseWithMetadata<T>> {
    const response = await requestWithMetadata<{
      data?: T;
      error?: number;
      status?: string | number;
      message?: string;
    }>(path, init);
    if (response.data && response.data.error === 1) {
      const status = Number(response.data.status) || 200;
      throw new ApiError(status, response.data.message ?? 'API error', response.data);
    }
    return { data: response.data.data as T, dataSource: response.dataSource };
  }

  return {
    baseUrl,
    requestWithMetadata,
    request: async <T,>(path: string, init?: RequestInit) => (await requestWithMetadata<T>(path, init)).data,
    dataWithMetadata,
    data: async <T,>(path: string, init?: RequestInit) => (await dataWithMetadata<T>(path, init)).data,
  };
}
